import React, { useEffect, useState } from 'react';

import {
  Money,
  ReferralUser,
  ReferralUserList as ReferralUserListModel,
  WithdrawRequest,
} from 'api-client';
import {
  AccountNotReadyWarningSection,
  Button,
  Checkbox,
  Container,
  CopyToClipboard,
  PrimaryButton,
  ReferralConfirmationModal,
  ReferralPayout,
  ReferralUserList,
  Section,
} from 'components';
import { PrimaryButtonColor } from 'components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import { Filter } from 'components/pages/transactions/TransactionsPartialFilter/TransactionsPartialFilter';
import apiClientConfig from 'configs/app';
import { createPremiumManager, createReferralManager } from 'di';
import { useErrorCallback, useProfileData } from 'hooks';
import { useDispatch } from 'react-redux';
import { setReferralEnabled } from 'store/profile';
import { v4 } from 'uuid';

import styles from './ReferralProgram.module.scss';

const USERS_PER_PAGE = 10;
const WITHDRAWALS_PER_PAGE = 25;
const WITHDRAWALS_MAX_PAGES = 20;

const ReferralProgram = () => {
  const profileData = useProfileData();
  const dispatch = useDispatch();
  const isUserNotVerified = !profileData.lenderVerified && !profileData.hasIdentificationDraft;
  const referralLink = profileData.paymentCode
    ? `${apiClientConfig.publicBaseUrl}/enter/?r=${profileData.paymentCode}`
    : '';

  const [confirmationUuid, setConfirmationUuid] = useState<string | undefined>(undefined);
  const [referralModalActive, setReferralModalActive] = useState(false);
  const [isRequestingCode, setIsRequestingCode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [users, setUsers] = useState<ReferralUser[]>([]);
  const [totalAccrued, setTotalAccrued] = useState<Money>(new Money(0, 'RUB'));
  const [totalAvailable, setTotalAvailable] = useState<Money>(new Money(0, 'RUB'));
  const [withdrawals, setWithdrawals] = useState<WithdrawRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [checkedConditions, setCheckedConditions] = useState({
    loyaltyProgram: false,
    goodSharing: false,
  });

  const handleCheckboxChange = (name: keyof typeof checkedConditions) => {
    setCheckedConditions((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const errorCallback = useErrorCallback();

  const handleRequestCode = async () => {
    if (isRequestingCode) return;

    setIsRequestingCode(true);
    try {
      const uuid = v4();
      setConfirmationUuid(uuid);
      const manager = await createPremiumManager();
      await manager.sendCode(uuid);
      setReferralModalActive(true);
    } catch (e) {
      setReferralModalActive(false);
    }
  };

  const loadUsers = async (page: number, append: boolean): Promise<void> => {
    setLoading(true);
    try {
      const filter: Filter = {
        search: '',
        dateFrom: '',
        dateTo: '',
        transactionDirection: null,
        projectUuids: [],
      };
      const manager = await createReferralManager();
      const response: ReferralUserListModel = await manager.getUserList(
        page,
        USERS_PER_PAGE,
        filter
      );

      dispatch(setReferralEnabled(profileData?.referralEnabled ?? false));
      setUsers((prev) => (append ? [...prev, ...response.users.items] : response.users.items));
      setTotalAccrued(response.totalAccrued);
      setTotalAvailable(response.totalAvailable);
      setTotalCount(response.users.totalCount);
    } catch (err: any) {
      await errorCallback(err);
    } finally {
      setLoading(false);
    }
  };

  const loadWithdrawals = async (): Promise<void> => {
    try {
      const manager = await createReferralManager();
      const firstPage = await manager.getWithdrawList(1, WITHDRAWALS_PER_PAGE);
      const items = [...firstPage.items];
      const pagesCount = Math.min(
        Math.ceil(firstPage.totalCount / WITHDRAWALS_PER_PAGE),
        WITHDRAWALS_MAX_PAGES
      );

      for (let page = 2; page <= pagesCount; page++) {
        const nextPage = await manager.getWithdrawList(page, WITHDRAWALS_PER_PAGE);
        items.push(...nextPage.items);
      }

      setWithdrawals(items);
    } catch (err: any) {
      await errorCallback(err);
    }
  };

  useEffect(() => {
    const load = async () => {
      await loadUsers(1, false);
      await loadWithdrawals();
    };
    load();
  }, []);

  const handleUsersLoadMore = () => {
    const nextPage = usersPage + 1;
    setUsersPage(nextPage);
    loadUsers(nextPage, true);
  };

  const handleUsersReset = () => {
    setUsersPage(1);
    loadUsers(1, false);
  };

  const conditionsNote = (
    <span className={styles.note}>
      Порядок начисления вознаграждений закреплен в условиях{' '}
      <a
        href="https://cdn.conomica.ru/file/3170a/a33/00cc-cfe28/usloviya-akcii-delimsya-xorosim.pdf"
        target="_blank"
        rel="noreferrer noopener"
      >
        Акции «Делимся хорошим»
      </a>
      , применяемой в рамках{' '}
      <a
        href="https://cdn.conomica.ru/file/3170a/a33/00ec-1cf31/usloviya-programmy-klientskoi-loyalnosti-onlain-servisa-konomika.pdf"
        target="_blank"
        rel="noreferrer noopener"
      >
        Программы клиентской лояльности Онлайн-сервиса Кономика
      </a>
      .
    </span>
  );

  return (
    <Section>
      <Container>
        <AccountNotReadyWarningSection />
        <div className={styles.referral}>
          <div className={styles.mainContent}>
            <div className={styles.intro}>
              <h1 className={styles.title}>Реферальная программа</h1>
              <div className={styles.description}>
                <p>Поделитесь тем, чему доверяете — растите вместе с «Кономикой»!</p>
                <p>
                  Если вам близок подход «Кономики» и вы готовы рекомендовать нас — реферальная
                  программа именно для вас.
                </p>
                <p>
                  За каждую инвестицию, совершённую по вашей рекомендации, вы получаете реферальный
                  доход в размере 1% размера участия реферала в сделках, без ограничения по срокам.
                </p>
                <p>Делитесь тем, что работает — вместе сильнее.</p>
              </div>

              {profileData.referralEnabled ? (
                <>
                  <div className={styles.linkRow}>
                    <span className={styles.link}>
                      {referralLink ? referralLink : 'Код ещё не загружен'}
                    </span>
                    <CopyToClipboard textToCopy={referralLink} withDescription />
                  </div>
                  <span className={styles.note}>
                    Ссылка действует бессрочно и рассчитана на неограниченное количество
                    приглашений.
                  </span>
                  {conditionsNote}
                </>
              ) : (
                <>
                  {conditionsNote}
                  <div className={styles.conditions}>
                    <Checkbox
                      checked={checkedConditions.loyaltyProgram}
                      onChange={() => handleCheckboxChange('loyaltyProgram')}
                    >
                      <span className={styles.note}>
                        Ознакомлен и согласен с условиями «Программы клиентской лояльности
                        Онлайн-сервиса Кономика»
                      </span>
                    </Checkbox>
                    <Checkbox
                      checked={checkedConditions.goodSharing}
                      onChange={() => handleCheckboxChange('goodSharing')}
                    >
                      <span className={styles.note}>
                        Ознакомлен и согласен с условиями Акции «Делимся хорошим»
                      </span>
                    </Checkbox>
                  </div>
                  <PrimaryButton color={PrimaryButtonColor.GREEN} className={styles.requestButton}>
                    <Button
                      onClick={handleRequestCode}
                      disabled={
                        isUserNotVerified ||
                        isRequestingCode ||
                        !checkedConditions.loyaltyProgram ||
                        !checkedConditions.goodSharing
                      }
                    >
                      {isRequestingCode ? 'Отправка запроса...' : 'Запросить реферальный код'}
                    </Button>
                  </PrimaryButton>
                </>
              )}
            </div>

            <ReferralUserList
              users={users}
              loading={loading}
              page={usersPage}
              totalCount={totalCount}
              onLoadMore={handleUsersLoadMore}
              onReset={handleUsersReset}
            />
          </div>

          <div className={styles.sidebar}>
            <ReferralPayout
              totalAccrued={totalAccrued}
              totalAvailable={totalAvailable}
              withdrawals={withdrawals}
            />
          </div>
        </div>

        {confirmationUuid && (
          <ReferralConfirmationModal
            active={referralModalActive}
            onClose={() => setReferralModalActive(false)}
            confirmationUuid={confirmationUuid}
            onSuccess={async () => {
              const manager = await createReferralManager();
              await manager.enableReferral();
              setReferralModalActive(false);
              dispatch(setReferralEnabled(true));
            }}
          />
        )}
      </Container>
    </Section>
  );
};

export default ReferralProgram;
